import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { forkJoin } from 'rxjs'

import { BaseComponent } from '../../shared'
import { SwpppMapComponent } from './swppp-map.component'

interface JobOption {
    id: string
    name: string
}

interface GeocodeHit {
    display_name: string
    lat: string
    lon: string
}

@Component({
    selector: 'app-swppp-site-wizard',
    templateUrl: './swppp-site-wizard.component.html',
    standalone: true,
    imports: [FormsModule, SwpppMapComponent],
})
export class SwpppSiteWizardComponent extends BaseComponent implements OnInit {
    step = 1
    jobs: JobOption[] = []
    job = ''
    name = ''
    latitude = 40.3
    longitude = -111.7
    address = ''
    search = ''
    hits: GeocodeHit[] = []
    acreage: number | null = null
    disturbedAcreage: number | null = null
    ms4 = ''
    contacts: { name: string; phone: string; email: string }[] = []
    saving = false
    error = ''

    constructor(public activeModal: NgbActiveModal) {
        super()
    }

    ngOnInit() {
        this.services.data.vpJobs.queryAllByAction('GetActiveAndSoftClose').subscribe(jobs => {
            const list = (jobs as JobOption[]) ?? []
            this.jobs = list.sort((a, b) => a.id.trim().localeCompare(b.id.trim(), undefined, { numeric: true }))
            this.initialLoadingComplete()
        })
    }

    jobChanged() {
        const selected = this.jobs.find(job => job.id === this.job)
        if (selected) this.name = `${selected.id.trim()} ${selected.name}`.trim()
    }

    searchAddress() {
        if (!this.search.trim()) return
        this.services.data.get<GeocodeHit[]>('/api/SwpppSite/Geocode?q=' + encodeURIComponent(this.search)).subscribe(hits => {
            this.hits = hits ?? []
        })
    }

    useHit(hit: GeocodeHit) {
        this.latitude = Number(hit.lat)
        this.longitude = Number(hit.lon)
        this.address = hit.display_name
        this.hits = []
    }

    useDevice() {
        navigator.geolocation.getCurrentPosition(position => {
            this.latitude = position.coords.latitude
            this.longitude = position.coords.longitude
        })
    }

    onPicked(point: { latitude: number; longitude: number }) {
        this.latitude = point.latitude
        this.longitude = point.longitude
        this.services.data
            .get<{ display_name?: string }>('/api/SwpppSite/ReverseGeocode?lat=' + point.latitude + '&lon=' + point.longitude)
            .subscribe(result => {
                if (result?.display_name) this.address = result.display_name
            })
    }

    addContact() {
        if (this.contacts.length < 3) this.contacts.push({ name: '', phone: '', email: '' })
    }

    create() {
        this.saving = true
        this.error = ''
        const site = {
            id: crypto.randomUUID(),
            job: this.job,
            name: this.name,
            latitude: this.latitude,
            longitude: this.longitude,
            address: this.address,
            acreage: this.acreage,
            disturbedAcreage: this.disturbedAcreage,
            ms4: this.ms4,
            isActive: true,
            publicToken: crypto.randomUUID(),
            archived: false,
            createdOn: new Date().toISOString(),
        }
        this.services.data.swpppSites.add(site as any).subscribe({
            next: saved => {
                const calls = this.contacts
                    .filter(contact => contact.name || contact.email || contact.phone)
                    .map(contact =>
                        this.services.data.swpppSitePeople.add({
                            id: crypto.randomUUID(),
                            siteId: saved.id,
                            name: contact.name,
                            email: contact.email,
                            phone: contact.phone,
                            role: 'SiteContact',
                            isPublicContact: true,
                            receivesReports: false,
                            requiresActionSignoff: false,
                            archived: false,
                            createdOn: new Date().toISOString(),
                        } as any),
                    )
                if (calls.length === 0) {
                    this.activeModal.close(saved)
                    return
                }
                forkJoin(calls).subscribe({
                    next: () => this.activeModal.close(saved),
                    error: () => this.activeModal.close(saved),
                })
            },
            error: () => {
                this.saving = false
                this.error = 'The site could not be saved.'
            },
        })
    }
}
